import { cn } from "../../utils/common/cn";
import type { SegmentCredibility } from "../../pages/PortfolioPerformance/lib/api/loss-calibration";

function peerLabel(peers: string[]) {
  if (peers.length === 0) return "—";
  const shown = peers.slice(0, 3).join(", ");
  const rest = peers.length - 3;
  return rest > 0 ? `${shown} and ${rest} more` : shown;
}

const pct = (value: number) => `${value.toFixed(2)}%`;

export function CredibilityTable({
  rows,
  highlightSegmentId,
  sandboxActive,
}: {
  rows: SegmentCredibility[];
  highlightSegmentId?: string | undefined;
  sandboxActive: boolean;
}) {
  return (
    <div
      className={cn(
        "overflow-x-auto border border-border transition-colors",
        sandboxActive ? "bg-amber-dim" : "bg-card",
      )}
    >
      <table className="w-full min-w-[1100px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {[
              "Segment",
              "n (matched contracts)",
              "Own Rate",
              "Tier",
              "Pooled With",
              "Peer Rate",
              "Z",
              "Blended Rate",
            ].map((label, i) => (
              <th
                key={label}
                className={cn(
                  "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                  i === 0 || i === 3 || i === 4 ? "text-left" : "text-right",
                )}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const highlighted = highlightSegmentId === row.segmentId;
            return (
              <tr
                key={row.segmentId}
                id={`segment-${row.segmentId.replace(/\s+/g, "-").toLowerCase()}`}
                className={cn(
                  "border-b border-border/60 last:border-b-0",
                  highlighted && "bg-primary/10 outline outline-1 outline-primary",
                )}
              >
                <td className="px-3 py-2.5">
                  <span className="font-medium text-foreground">{row.label}</span>
                  <span className="ml-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {row.level}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {row.matchedContracts.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{pct(row.ownRate)}</td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center border border-border bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                    {row.tier}
                  </span>
                </td>
                <td className="max-w-[280px] px-3 py-2.5 text-xs text-muted-foreground">
                  {peerLabel(row.peers)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{pct(row.peerPooledRate)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  <span className="relative inline-flex min-w-[110px] items-center justify-end overflow-hidden bg-secondary/60 px-2 py-1">
                    <span
                      className="absolute inset-y-0 left-0 bg-primary/35"
                      style={{ width: `${Math.min(100, Math.max(0, row.z * 100))}%` }}
                      aria-hidden
                    />
                    <span className="relative">{Math.round(row.z * 100)}% own weight</span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                  {pct(row.blendedRate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
